/**
 * Swipe — 가로 스와이프 캐러셀 (재사용 모듈, 다중 인스턴스).
 *
 * 마크업 (scope 안에 아래 요소들이 있으면 됨 — 위치/중첩은 자유):
 *   <div class="b-swipe__track">  .b-swipe__card * N  </div>   // 필수 (translateX 대상)
 *   <button class="b-swipe__prev"> / <button class="b-swipe__next">   // 선택 (좌우 화살표)
 *   <div class="b-swipe__nav">  .b-swipe__dot * N  </div>            // 선택 (인디케이터)
 *
 * 사용:
 *   $j.Ui.Swipe.init('.b-feat');          // scope 안의 첫 트랙을 캐러셀로
 *   const sw = $j.Ui.Swipe.init($el);     // 컨트롤러 반환 { to, refresh, destroy }
 *
 * 옵션:
 *   threshold   스와이프 판정 거리(px, 기본 40)
 *   onChange    카드가 바뀔 때 호출 (index, $card) — 초기 렌더에서는 호출하지 않음
 *   syncHeight  true면 트랙 높이를 매번 "현재 보이는 카드"의 실제 높이로 맞춘다(기본 꺼짐).
 *               카드 높이가 제각각인 캐러셀에서 트랙이 가장 긴 카드 높이로 고정돼(flex 기본 동작)
 *               짧은 카드 아래 빈 공간이 남는 것을 막는다.
 *               ⚠ 카드가 자기 내용 높이를 가져야 잴 수 있다 — 트랙에 align-items:flex-start 를 준다
 *               (기본 stretch 면 모든 카드가 트랙 높이로 늘어나 재도 같은 값이 나온다).
 *               높이는 render 때만 잰다(카드 이동·리사이즈·refresh()). 그 뒤 내용이 바뀌면 refresh() 를 부른다.
 *
 * - 카드 2개 미만이면 화살표/인디케이터를 숨기고 바인딩을 생략한다.
 * - 터치 드래그 + 화살표 + dot 클릭 + 리사이즈 재측정 지원.
 * - DOM 이 분리되면(BS 닫힘 등) 리사이즈 핸들러가 스스로 해제된다.
 */
let _seq = 0;

export default class Swipe {
    init(scope, opts = {}) {
        const $scope = $(scope);
        if (!$scope.length) return null;

        const $track = $scope.find('.b-swipe__track').first();
        if (!$track.length) return null;

        const $cards = $track.children();
        const total  = $cards.length;
        const $dots  = $scope.find('.b-swipe__dot');
        const $prev  = $scope.find('.b-swipe__prev');
        const $next  = $scope.find('.b-swipe__next');
        const ns     = '.bswipe' + (++_seq);
        const threshold = opts.threshold || 40;

        // 카드 1개 이하 — 네비 숨기고 종료
        if (total < 2) {
            $dots.add($prev).add($next).hide();
            return null;
        }

        let index = 0;
        let lastIndex = 0;

        // 카드 간 이동 거리 (카드폭 + gap) — 렌더 시점 실측
        const step = () => {
            const a = $cards.eq(0)[0];
            const b = $cards.eq(1)[0];
            return (b.offsetLeft - a.offsetLeft) || a.getBoundingClientRect().width;
        };

        const render = () => {
            index = Math.max(0, Math.min(index, total - 1));
            $track.css('transform', `translateX(${-index * step()}px)`);
            if (opts.syncHeight) $track.css('height', $cards.eq(index)[0].offsetHeight + 'px');
            $dots.removeClass('is-on').eq(index).addClass('is-on');
            $prev.toggleClass('is-disabled', index === 0);
            $next.toggleClass('is-disabled', index === total - 1);

            if (index !== lastIndex) {
                lastIndex = index;
                if (opts.onChange) opts.onChange(index, $cards.eq(index));
            }
        };

        const to = (i) => { index = i; render(); };

        $dots.off(ns).on('click' + ns, function () { to($dots.index(this)); });
        $prev.off(ns).on('click' + ns, () => to(index - 1));
        $next.off(ns).on('click' + ns, () => to(index + 1));

        // 터치 드래그
        let startX = 0, dragging = false;
        $track.off(ns)
            .on('touchstart' + ns, (e) => { startX = e.originalEvent.touches[0].clientX; dragging = true; })
            .on('touchend' + ns, (e) => {
                if (!dragging) return;
                dragging = false;
                const dx = e.originalEvent.changedTouches[0].clientX - startX;
                if (dx < -threshold) to(index + 1);
                else if (dx > threshold) to(index - 1);
                else render();
            });

        // 리사이즈 재측정 — DOM 분리되면 자동 해제
        $(window).off(ns).on('resize' + ns, () => {
            if (!$track.closest('body').length) { $(window).off(ns); return; }
            render();
        });

        render();

        return {
            to,
            refresh: render,
            destroy() {
                $dots.off(ns); $prev.off(ns); $next.off(ns); $track.off(ns); $(window).off(ns);
                if (opts.syncHeight) $track.css('height', '');   // syncHeight 가 박은 높이 회수
            },
        };
    }
}
